/**
 * @Author: Claude Code
 * @Date: 2025-11-21
 * @FilePath: src/store/modules/videoProgress.js
 * @Description: 视频进度 Vuex 模块 - 支持多设备订阅和批量操作
 */
import Vue from 'vue'
import videoProgressService from '@/services/videoProgressService'

// 最大订阅数量限制
const MAX_SUBSCRIPTIONS = 20

const state = {
  // SignalR 连接状态
  connected: false,
  connecting: false,
  connectionId: null,
  connectionState: 'Disconnected',

  // Hub URL (从环境变量或配置获取)
  hubUrl: process.env.VUE_APP_SIGNALR_HUB_URL || 'http://localhost:37494/hubs/video-progress',

  // 进度数据: { imei: [progressList] }
  progressMap: {},

  // 当前订阅的设备列表
  subscribedDevices: [],

  // 最大订阅数量
  maxSubscriptions: MAX_SUBSCRIPTIONS,
}

const getters = {
  /**
   * 获取指定设备的进度列表
   */
  getProgressList: (state) => (imei) => {
    return state.progressMap[imei] || []
  },

  /**
   * 获取指定设备的单个文件进度
   */
  getFileProgress: (state) => (imei, fileName) => {
    const list = state.progressMap[imei] || []
    return list.find((item) => item.FileName === fileName)
  },

  /**
   * 检查设备是否已订阅
   */
  isSubscribed: (state) => (imei) => {
    return state.subscribedDevices.includes(imei)
  },

  /**
   * 获取所有进度数据
   */
  getAllProgress: (state) => {
    return state.progressMap
  },

  /**
   * 检查是否连接
   */
  isConnected: (state) => {
    return state.connected
  },

  /**
   * 获取设备的整体上传进度 (百分比)
   */
  getDeviceProgress: (state, getters) => (imei) => {
    const list = getters.getProgressList(imei)
    if (list.length === 0) return 0

    const total = list.reduce((sum, item) => sum + item.Percentage, 0)
    return Math.round(total / list.length)
  },

  /**
   * 获取设备状态 (uploading/completed/failed/pending)
   */
  getDeviceStatus: (state, getters) => (imei) => {
    const list = getters.getProgressList(imei)
    if (list.length === 0) return null

    const hasUploading = list.some((item) => item.Status === 'uploading')
    const hasFailed = list.some((item) => item.Status === 'failed')
    const allCompleted = list.every((item) => item.Status === 'completed')

    if (hasFailed) return 'failed'
    if (allCompleted) return 'completed'
    if (hasUploading) return 'uploading'
    return 'pending'
  },

  /**
   * 获取所有设备的统计信息
   */
  getAllDevicesStats: (state) => {
    const devices = state.subscribedDevices
    return {
      total: devices.length,
      uploading: devices.filter((imei) => {
        const list = state.progressMap[imei] || []
        return list.some((item) => item.Status === 'uploading')
      }).length,
      completed: devices.filter((imei) => {
        const list = state.progressMap[imei] || []
        return list.length > 0 && list.every((item) => item.Status === 'completed')
      }).length,
      failed: devices.filter((imei) => {
        const list = state.progressMap[imei] || []
        return list.some((item) => item.Status === 'failed')
      }).length,
    }
  },

  /**
   * 检查是否达到订阅上限
   */
  isSubscriptionLimitReached: (state) => {
    return state.subscribedDevices.length >= state.maxSubscriptions
  },
}

const mutations = {
  /**
   * 设置连接状态
   */
  SET_CONNECTED(state, connected) {
    state.connected = connected
  },

  /**
   * 设置连接中状态
   */
  SET_CONNECTING(state, connecting) {
    state.connecting = connecting
  },

  /**
   * 设置连接ID
   */
  SET_CONNECTION_ID(state, connectionId) {
    state.connectionId = connectionId
  },

  /**
   * 设置连接状态枚举
   */
  SET_CONNECTION_STATE(state, connectionState) {
    state.connectionState = connectionState
  },

  /**
   * 设置 Hub URL
   */
  SET_HUB_URL(state, hubUrl) {
    state.hubUrl = hubUrl
  },

  /**
   * 更新进度数据 (使用 Vue.set 确保响应式)
   */
  UPDATE_PROGRESS(state, { imei, progressData }) {
    // 初始化设备进度列表
    if (!state.progressMap[imei]) {
      Vue.set(state.progressMap, imei, [])
    }

    const list = state.progressMap[imei]
    const index = list.findIndex((item) => item.FileName === progressData.FileName)

    if (index >= 0) {
      // 使用 Vue.set 更新数组元素，确保响应式
      Vue.set(list, index, progressData)
    } else {
      // 添加新项
      list.push(progressData)
    }
  },

  /**
   * 批量更新进度数据
   */
  BATCH_UPDATE_PROGRESS(state, updates) {
    updates.forEach(({ imei, progressData }) => {
      if (!state.progressMap[imei]) {
        Vue.set(state.progressMap, imei, [])
      }

      const list = state.progressMap[imei]
      const index = list.findIndex((item) => item.FileName === progressData.FileName)

      if (index >= 0) {
        Vue.set(list, index, progressData)
      } else {
        list.push(progressData)
      }
    })
  },

  /**
   * 清除指定设备的进度
   */
  CLEAR_PROGRESS(state, imei) {
    if (state.progressMap[imei]) {
      Vue.delete(state.progressMap, imei)
    }
  },

  /**
   * 清除所有进度
   */
  CLEAR_ALL_PROGRESS(state) {
    state.progressMap = {}
  },

  /**
   * 添加订阅设备
   */
  ADD_SUBSCRIBED_DEVICE(state, imei) {
    if (!state.subscribedDevices.includes(imei)) {
      state.subscribedDevices.push(imei)
    }
  },

  /**
   * 移除订阅设备
   */
  REMOVE_SUBSCRIBED_DEVICE(state, imei) {
    const index = state.subscribedDevices.indexOf(imei)
    if (index >= 0) {
      state.subscribedDevices.splice(index, 1)
    }
  },

  /**
   * 清除所有订阅
   */
  CLEAR_SUBSCRIBED_DEVICES(state) {
    state.subscribedDevices = []
  },

  /**
   * 设置最大订阅数量
   */
  SET_MAX_SUBSCRIPTIONS(state, max) {
    state.maxSubscriptions = max
  },
}

const actions = {
  /**
   * 连接 SignalR Hub
   */
  async connect({ commit, state }) {
    if (state.connected) {
      console.log('[Vuex] 已经连接，无需重复连接')
      return true
    }

    commit('SET_CONNECTING', true)

    try {
      const connected = await videoProgressService.connect(state.hubUrl)
      commit('SET_CONNECTED', connected)

      if (connected) {
        const connectionId = videoProgressService.getConnectionId()
        const connectionState = videoProgressService.getConnectionState()
        commit('SET_CONNECTION_ID', connectionId)
        commit('SET_CONNECTION_STATE', connectionState)
      }

      return connected
    } catch (error) {
      console.error('[Vuex] 连接失败:', error)
      commit('SET_CONNECTED', false)
      throw error
    } finally {
      commit('SET_CONNECTING', false)
    }
  },

  /**
   * 订阅设备进度
   */
  async subscribe({ commit, dispatch, state, getters }, { imei, autoConnect = true }) {
    // 检查订阅数量限制
    if (getters.isSubscriptionLimitReached && !getters.isSubscribed(imei)) {
      throw new Error(`订阅数量不能超过 ${state.maxSubscriptions} 个`)
    }

    // 检查是否已订阅
    if (getters.isSubscribed(imei)) {
      console.warn(`[Vuex] 设备 ${imei} 已订阅，跳过`)
      return true
    }

    try {
      // 自动连接
      if (autoConnect && !videoProgressService.checkConnection()) {
        await dispatch('connect')
      }

      // 订阅进度，回调函数中更新 Vuex
      await videoProgressService.subscribeProgress(imei, (progressData) => {
        commit('UPDATE_PROGRESS', { imei, progressData })
      })

      commit('ADD_SUBSCRIBED_DEVICE', imei)

      return true
    } catch (error) {
      console.error('[Vuex] 订阅失败:', error)
      throw error
    }
  },

  /**
   * 批量订阅多个设备
   */
  async batchSubscribe({ dispatch }, { imeis, autoConnect = true }) {
    if (!Array.isArray(imeis) || imeis.length === 0) {
      throw new Error('imeis 必须是非空数组')
    }

    const results = []

    // 首次自动连接
    if (autoConnect) {
      try {
        await dispatch('connect')
      } catch (error) {
        console.error('[Vuex] 批量订阅时连接失败:', error)
        throw error
      }
    }

    // 依次订阅每个设备
    for (const imei of imeis) {
      try {
        await dispatch('subscribe', { imei, autoConnect: false })
        results.push({ imei, success: true })
      } catch (error) {
        console.error(`[Vuex] 批量订阅设备 ${imei} 失败:`, error)
        results.push({ imei, success: false, error: error.message })
      }
    }

    return results
  },

  /**
   * 取消订阅
   */
  async unsubscribe({ commit }, imei) {
    try {
      await videoProgressService.unsubscribeProgress(imei)
      commit('REMOVE_SUBSCRIBED_DEVICE', imei)
      commit('CLEAR_PROGRESS', imei)
    } catch (error) {
      console.error('[Vuex] 取消订阅失败:', error)
      throw error
    }
  },

  /**
   * 批量取消订阅
   */
  async batchUnsubscribe({ dispatch }, imeis) {
    if (!Array.isArray(imeis) || imeis.length === 0) {
      throw new Error('imeis 必须是非空数组')
    }

    const results = []

    for (const imei of imeis) {
      try {
        await dispatch('unsubscribe', imei)
        results.push({ imei, success: true })
      } catch (error) {
        console.error(`[Vuex] 批量取消订阅设备 ${imei} 失败:`, error)
        results.push({ imei, success: false, error: error.message })
      }
    }

    return results
  },

  /**
   * 断开连接
   */
  async disconnect({ commit }) {
    try {
      await videoProgressService.disconnect()
      commit('SET_CONNECTED', false)
      commit('SET_CONNECTION_ID', null)
      commit('SET_CONNECTION_STATE', 'Disconnected')
      commit('CLEAR_SUBSCRIBED_DEVICES')
      commit('CLEAR_ALL_PROGRESS')
    } catch (error) {
      console.error('[Vuex] 断开连接失败:', error)
      throw error
    }
  },

  /**
   * 获取订阅者数量 (服务端)
   */
  async getSubscriberCount() {
    try {
      return await videoProgressService.getSubscriberCount()
    } catch (error) {
      console.error('[Vuex] 获取订阅者数量失败:', error)
      return 0
    }
  },

  /**
   * 清除指定设备进度
   */
  clearProgress({ commit }, imei) {
    commit('CLEAR_PROGRESS', imei)
  },

  /**
   * 清除所有进度
   */
  clearAllProgress({ commit }) {
    commit('CLEAR_ALL_PROGRESS')
  },

  /**
   * 更新 Hub URL
   */
  updateHubUrl({ commit }, hubUrl) {
    commit('SET_HUB_URL', hubUrl)
  },

  /**
   * 设置最大订阅数量
   */
  setMaxSubscriptions({ commit }, max) {
    if (typeof max === 'number' && max > 0) {
      commit('SET_MAX_SUBSCRIPTIONS', max)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
